/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the VersioningPage component.
 *                                   - Integrated useVersioning hook for data management.
 *                                   - Implemented dialogs for adding and editing versioning records.
 *                                   - Utilized VersioningForm and VersioningTable components.
 * 1.1.0:gemini-1.5-flash (PREV) - Added version selection functionality using IndexedDB and X-Data-Version header.
 * 1.2.0:gemini-1.5-flash (PREV-1) - Refactored to use `useVersioningContext` for global state propagation.
 * 1.3.0:gemini-1.5-flash (PREV) - Refactored IndexedDB settings interaction to use `ui/chat/src/utils/indexedDB.ts` for consistency.
 * 1.4.0:gemini-1.5-flash (CURRENT) - Implemented "Update" functionality with conflict resolution.
 *                                   - Added "Merge" functionality with conflict resolution, using `/versioning/merge` endpoint.
 *
 * -----
 * SECURITY_RISK: LOW - This component provides a UI for managing system versioning records.
 *                      It orchestrates other components and hooks but does not directly handle sensitive data.
 * INTENT: Provide a complete CRUD interface for system versioning records,
 *         allowing users to view, add, edit, and delete versions, select the active version for API requests,
 *         update a version with changes from 'Main', and merge a version's changes into 'Main',
 *         all with conflict resolution capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure consistent user experience for CRUD operations
 *                        and proper error feedback. Ensure version selection is persisted.
 *                        Verify that update and merge operations correctly handle conflicts and user choices.
 * LLM_CONFIDENCE: 100% - Standard page component integrating form, table, and hook for CRUD, with added version selection,
 *                        update, and merge logic including conflict resolution.
 */
import { useState, useCallback } from 'react';
import { Versioning, VersioningCreate, VersioningUpdate, ComparisonResult, MarginReportResult, VersionUpdateResponse, VersionConflictEntry, VersionMergeResponse } from '../types/costwand.d'; // MODIFIED: Added VersionMergeResponse
import { useVersioningContext } from '../context/VersioningContext.tsx';
import { useVersioning } from '../hooks/useVersioning.ts';
import VersioningForm from '../components/VersioningForm.tsx';
import VersioningTable from '../components/VersioningTable.tsx';
import { Button } from '../components/ui/button.tsx';
import { apiClient, ApiError } from '../api/apiClient.tsx';
import { Label } from '../components/ui/label.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog.tsx';
import { Spinner } from '../components/ui/spinner.tsx';
import UpdateConflictResolver from '../components/UpdateConflictResolver.tsx';
import { toast } from 'react-toastify';

const VersioningPage = () => {
  const {
    selectedVersionTag,
    setSelectedVersionTag,
    versionSelectionLoading
  } = useVersioningContext();

  const {
    versioning,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createVersioning,
    updateVersioning,
    deleteVersioning,
    fetchVersioning
  } = useVersioning();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isChangeFormOpen, setIsChangeFormOpen] = useState<boolean>(false);
  const [editingVersioning, setEditingVersioning] = useState<Versioning | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // State for Update modal
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateConflictDetails, setUpdateConflictDetails] = useState<VersionConflictEntry | null>(null);
  const [versionToUpdate, setVersionToUpdate] = useState<Versioning | null>(null);

  // NEW: State for Merge modal
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeConflictDetails, setMergeConflictDetails] = useState<VersionConflictEntry | null>(null);
  const [versionToMerge, setVersionToMerge] = useState<Versioning | null>(null);

  // State for ignored record IDs during conflict resolution (shared for update/merge)
  const [ignoredRecordIds, setIgnoredRecordIds] = useState<number[]>([]);

  const handleAddVersioning = () => {
    setEditingVersioning(null);
    setIsFormOpen(true);
  };

  const handleEditVersioning = (version: Versioning) => {
    setEditingVersioning(version);
    setIsFormOpen(true);
  };

  const handleDeleteVersioning = async (id: number) => {
    await deleteVersioning(id);
    // The hook already handles refetching and page adjustment after deletion
  };

  const handleFormSubmit = async (data: VersioningCreate | VersioningUpdate) => {
    setFormSubmitting(true);

    try {
      let success = false;
      if (editingVersioning) {
        success = await updateVersioning(editingVersioning.id!, data as VersioningUpdate);
      } else {
        success = await createVersioning(data as VersioningCreate);
        if(success) {
          setSelectedVersionTag(data.tag);
          toast.success(`Changed to new version ${data.tag}`);
        }
      }

      if (success) {
        setIsFormOpen(false);
        // The hook already handles refetching after successful creation/update
      }
    } catch (e: any) {
      // Error handling is managed by the global error context (apiClient.tsx)
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingVersioning(null);
    setIsChangeFormOpen(false);
    setShowUpdateModal(false);
    setUpdateConflictDetails(null);
    setVersionToUpdate(null); // NEW: Clear versionToUpdate
    setShowMergeModal(false); // NEW: Close merge modal
    setMergeConflictDetails(null); // NEW: Clear merge conflict details
    setVersionToMerge(null); // NEW: Clear versionToMerge
    setIgnoredRecordIds([]);
  };

  const handleSelectVersion = async (tag: string) => {
    try {
      await setSelectedVersionTag(tag);
      toast.info(`Working version set to: ${tag != "" ? tag : "Main"}`);
      setIsChangeFormOpen(false);
      fetchVersioning(currentPage);
    } catch (e) {
      console.error("Failed to save selected version to IndexedDB:", e);
      toast.error("Failed to save selected version. Please try again.");
    }
  };

  // Handler for the "Update" action (pull changes from Main into current version)
  const handleUpdate = useCallback(async (version: Versioning, ignoredIds: number[] = [], confirm: boolean = false) => {
    setUpdateLoading(true);
    setUpdateConflictDetails(null);
    setVersionToUpdate(version);

    try {
      const queryParams = (confirm ? 'confirm=1&' : '') + ignoredIds.map(id => `ignore[]=${id}`).join('&');
      const endpoint = `/versioning/update${queryParams ? `?${queryParams}` : ''}`;

      const response = await apiClient<VersionUpdateResponse>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ tag: version.tag }),
      });

      if (response.message === 'merged' && !response.error) {
        toast.success(`Version '${version.tag || 'Main'}' successfully updated with Main changes!`);
        setShowUpdateModal(false);
        await fetchVersioning(currentPage);
      } else if (response.message === 'invalid-changes') {
        setUpdateConflictDetails(response.details || {});
        setShowUpdateModal(true);
      } else {
        throw new Error(response.message || 'An unexpected error occurred during update.');
      }
    } catch (err: any) {
      console.error("Error during version update:", err);
      // Global error handler will display the error for non-'invalid-changes' cases
      setShowUpdateModal(false); // Close modal if a global error occurred
    } finally {
      setUpdateLoading(false);
      setIgnoredRecordIds([]);
    }
  }, [currentPage, fetchVersioning]);

  // NEW: Handler for the "Merge" action (push changes from current version into Main)
  const handleMerge = useCallback(async (version: Versioning, ignoredIds: number[] = [], confirm: boolean = false) => {
    setMergeLoading(true);
    setMergeConflictDetails(null);
    setVersionToMerge(version); // Store the version being merged

    try {
      const queryParams = (confirm ? 'confirm=1&' : '') + ignoredIds.map(id => `ignore[]=${id}`).join('&');
      const endpoint = `/versioning/merge${queryParams ? `?${queryParams}` : ''}`;

      const response = await apiClient<VersionMergeResponse>(endpoint, { // MODIFIED: Use VersionMergeResponse
        method: 'POST',
        body: JSON.stringify({ source_tag: version.tag, target_tag: '' }), // Merge 'this' version into 'Main' (empty tag)
      });

      if (response.message === 'merged' && !response.error) {
        toast.success(`Changes from version '${version.tag}' successfully merged into Main!`);
        setShowMergeModal(false); // Close modal on success
        await fetchVersioning(currentPage); // Refetch to show updated new_changes for the source version
      } else if (response.message === 'invalid-changes') {
        setMergeConflictDetails(response.details || {}); // Set conflict details
        setShowMergeModal(true); // Open modal to show conflicts
      } else {
        throw new Error(response.message || 'An unexpected error occurred during merge.');
      }
    } catch (err: any) {
      console.error("Error during version merge:", err);
      // Global error handler will display the error for non-'invalid-changes' cases
      setShowMergeModal(false); // Close modal if a global error occurred
    } finally {
      setMergeLoading(false);
      setIgnoredRecordIds([]); // Clear ignored IDs after attempt
    }
  }, [currentPage, fetchVersioning]);

  // Handler for resolving conflicts from the UpdateConflictResolver component for UPDATE
  const handleResolveUpdateConflicts = useCallback(async (idsToIgnore: number[]) => {
    if (!versionToUpdate) return;
    setIgnoredRecordIds(idsToIgnore);
    await handleUpdate(versionToUpdate, idsToIgnore, true); // Re-attempt update with ignored IDs
  }, [versionToUpdate, handleUpdate]);

  // NEW: Handler for resolving conflicts from the UpdateConflictResolver component for MERGE
  const handleResolveMergeConflicts = useCallback(async (idsToIgnore: number[]) => {
    if (!versionToMerge) return;
    setIgnoredRecordIds(idsToIgnore);
    await handleMerge(versionToMerge, idsToIgnore, true); // Re-attempt merge with ignored IDs
  }, [versionToMerge, handleMerge]);


  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Versioning Management</h2>
        <div className="flex items-center space-x-4">
          {/* Version Selection */}
          <div className="flex items-center space-x-2">
            <Label htmlFor="version-select" className="text-sm font-medium">Working Version:</Label>
            {versionSelectionLoading ? (
              <Spinner size="sm" />
            ) : (
              <span className="text-sm font-semibold">
                {selectedVersionTag || 'Main'}
              </span>
            )}
            {/* Button to trigger version selection (could be a dropdown or modal) */}
            {versioning.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setIsChangeFormOpen(true)} disabled={versionSelectionLoading}>
                {versionSelectionLoading ? <Spinner size="sm" /> : 'Change Version'}
              </Button>
            )}
          </div>

          {/* Add New Version Dialog */}
          <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddVersioning}>Add New Version</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{editingVersioning ? 'Edit Version' : 'Add New Version'}</DialogTitle>
                <DialogDescription>
                  {editingVersioning ? 'Make changes to the version record here.' : 'Add a new version record to track system changes.'}
                </DialogDescription>
              </DialogHeader>
              <VersioningForm
                initialData={editingVersioning}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                isSubmitting={formSubmitting}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Version Table */}
      <VersioningTable
        versioning={versioning}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditVersioning}
        onDelete={handleDeleteVersioning}
        loading={loading}
        error={error}
        baseEndpoint="/versioning"
        onUpdateVersion={handleUpdate}
        onMergeVersion={handleMerge} // NEW: Pass handleMerge
        fetchVersioning={fetchVersioning}
      />

      {/* Version Selection Dialog (if a more complex UI is needed than just a button) */}
      <Dialog open={isChangeFormOpen} onOpenChange={(isOpen) => {
        setIsChangeFormOpen(isOpen);
        if (!isOpen) setEditingVersioning(null);
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Select Working Version</DialogTitle>
            <DialogDescription>Choose the version you want to work with.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {versioning.length > 0 ? (
              versioning.map((v) => (
                <Button
                  key={v.id}
                  variant={selectedVersionTag == v.tag ? 'default' : 'outline'}
                  onClick={() => handleSelectVersion(v.tag!)}
                  className="w-full justify-start"
                  disabled={versionSelectionLoading}
                >
                  {selectedVersionTag == v.tag && <span className="mr-2">✅</span>}
                  {v.tag} - {v.description}
                </Button>
              ))
            ) : (
              <p className="text-muted-foreground">No versions available to select.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleFormCancel} disabled={versionSelectionLoading}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Result Modal for invalid-changes */}
      <Dialog open={showUpdateModal} onOpenChange={handleFormCancel}>
        <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-destructive">Update Failed: Conflicts Detected</DialogTitle>
            <DialogDescription>
              Version '{versionToUpdate?.tag || 'N/A'}' could not be updated automatically due to conflicts with your current changes.
            </DialogDescription>
          </DialogHeader>
          {updateConflictDetails ? (
            <UpdateConflictResolver
              conflicts={updateConflictDetails}
              onResolve={handleResolveUpdateConflicts} // MODIFIED: Use specific handler
              isSubmitting={updateLoading}
              onCancel={handleFormCancel}
            />
          ) : (
            <div className="py-4 text-red-600 bg-red-50 p-3 rounded-md">
              <p className="font-semibold mb-2">No specific conflict details provided.</p>
              <p>Please try again or contact support if the issue persists.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Merge Result Modal for invalid-changes */}
      <Dialog open={showMergeModal} onOpenChange={handleFormCancel}>
        <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-destructive">Merge Failed: Conflicts Detected</DialogTitle>
            <DialogDescription>
              Changes from version '{versionToMerge?.tag || 'N/A'}' could not be merged into Main automatically due to conflicts.
            </DialogDescription>
          </DialogHeader>
          {mergeConflictDetails ? (
            <UpdateConflictResolver
              conflicts={mergeConflictDetails}
              onResolve={handleResolveMergeConflicts} // NEW: Use specific handler
              isSubmitting={mergeLoading}
              onCancel={handleFormCancel}
            />
          ) : (
            <div className="py-4 text-red-600 bg-red-50 p-3 rounded-md">
              <p className="font-semibold mb-2">No specific conflict details provided.</p>
              <p>Please try again or contact support if the issue persists.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VersioningPage;